/** Initial state for Business Relocation RFQ (website). */

/** Max words for address and notes textareas (shown in UI counters). */
export const BR_ADDRESS_MAX_WORDS = 500;
export const BR_SPECIAL_NOTES_MAX_WORDS = 500;

export const BR_MAX_SUPPLEMENTAL_LOCATIONS = 8;

export function countWords(text) {
  const s = String(text ?? '').trim();
  if (!s) return 0;
  return s.split(/\s+/).filter(Boolean).length;
}

export const BR_WEIGHT_UNIT_OPTIONS = ['KG', 'LB', 'MT', 'TON'];

export const BR_VALUE_CURRENCY_OPTIONS = ['INR', 'USD', 'EUR', 'GBP'];

export function normalizePhoneDigits(phone) {
  return String(phone ?? '').replace(/\D/g, '');
}

export function isValidContactPhone(phone) {
  const digits = normalizePhoneDigits(phone);
  return digits.length >= 8 && digits.length <= 15;
}

export function isValidCompanyEmail(email) {
  const e = String(email ?? '').trim();
  return e.length > 3 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
}

export function isValidPositiveDecimal(value) {
  const s = String(value ?? '').trim();
  if (!s || !/^\d+(\.\d+)?$/.test(s)) return false;
  return Number(s) > 0;
}

export function isValidNonNegativeDecimal(value) {
  const s = String(value ?? '').trim();
  if (!s || !/^\d+(\.\d+)?$/.test(s)) return false;
  return Number(s) >= 0;
}

export function isValidPositiveInteger(value) {
  const s = String(value ?? '').trim();
  if (!s || !/^\d+$/.test(s)) return false;
  return Number(s) >= 1;
}

export function isValidPincode(value) {
  const s = String(value ?? '').trim();
  if (!s) return false;
  return /^[A-Za-z0-9][A-Za-z0-9\s-]{2,11}$/.test(s);
}

export function isValidPreferredMoveDate(value) {
  const s = String(value ?? '').trim();
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const d = new Date(`${s}T00:00:00`);
  return !Number.isNaN(d.getTime());
}

/**
 * Returns the first validation error message, or null if the form is valid.
 * @param {Record<string, unknown>} form
 */
export function getBusinessRelocationRfqValidationError(form) {
  if (!String(form.contactPerson ?? '').trim()) {
    return 'Please enter contact person.';
  }
  if (!String(form.contactNumber ?? '').trim()) {
    return 'Please enter contact number.';
  }
  if (!isValidContactPhone(form.contactNumber)) {
    return 'Please enter a valid contact number (8–15 digits).';
  }
  if (!String(form.companyName ?? '').trim()) {
    return 'Please enter company name.';
  }
  if (!String(form.companyEmail ?? '').trim()) {
    return 'Please enter company email.';
  }
  if (!isValidCompanyEmail(form.companyEmail)) {
    return 'Please enter a valid company email.';
  }
  if (!form.relocationScope) {
    return 'Please select type of relocation.';
  }
  if (!String(form.currentCountry ?? '').trim() || !String(form.currentAddress ?? '').trim()) {
    return 'Please complete current location (country and address).';
  }
  if (
    !String(form.currentState ?? '').trim() ||
    !String(form.currentCity ?? '').trim() ||
    !String(form.currentPincode ?? '').trim()
  ) {
    return 'Please complete current state, city, and pincode.';
  }
  if (!isValidPincode(form.currentPincode)) {
    return 'Please enter a valid current pincode/ZIP (3–12 characters).';
  }
  if (!String(form.destCountry ?? '').trim() || !String(form.destAddress ?? '').trim()) {
    return 'Please complete destination location (country and address).';
  }
  if (
    !String(form.destState ?? '').trim() ||
    !String(form.destCity ?? '').trim() ||
    !String(form.destPincode ?? '').trim()
  ) {
    return 'Please complete destination state, city, and pincode.';
  }
  if (!isValidPincode(form.destPincode)) {
    return 'Please enter a valid destination pincode/ZIP (3–12 characters).';
  }
  if (!String(form.totalWeightKg ?? '').trim()) {
    return 'Please enter total estimated weight.';
  }
  if (!isValidPositiveDecimal(form.totalWeightKg)) {
    return 'Total estimated weight must be a valid number greater than zero.';
  }
  if (!String(form.totalPackages ?? '').trim()) {
    return 'Please enter total number of packages.';
  }
  if (!isValidPositiveInteger(form.totalPackages)) {
    return 'Total number of packages must be a whole number of at least 1.';
  }
  if (!String(form.totalValueUsd ?? '').trim()) {
    return 'Please enter total estimated value.';
  }
  if (!isValidNonNegativeDecimal(form.totalValueUsd)) {
    return 'Total estimated value must be a valid number (zero or greater).';
  }
  if (!String(form.totalValueCurrency ?? '').trim()) {
    return 'Please select total estimated value currency.';
  }
  if (!String(form.preferredMoveDate ?? '').trim()) {
    return 'Please select preferred move date.';
  }
  if (!isValidPreferredMoveDate(form.preferredMoveDate)) {
    return 'Please enter a valid preferred move date.';
  }
  if (!form.urgencyLevel) {
    return 'Please select urgency level.';
  }
  if (!form.logisticsMode) {
    return 'Please select preferred logistics mode.';
  }
  if (!form.budgetRange) {
    return 'Please select budget range.';
  }
  if (countWords(form.currentAddress) > BR_ADDRESS_MAX_WORDS) {
    return `Complete Current Address must be ${BR_ADDRESS_MAX_WORDS} words or fewer.`;
  }
  if (countWords(form.destAddress) > BR_ADDRESS_MAX_WORDS) {
    return `Complete Destination Address must be ${BR_ADDRESS_MAX_WORDS} words or fewer.`;
  }
  if (countWords(form.specialNotes) > BR_SPECIAL_NOTES_MAX_WORDS) {
    return `Special Requirements & Notes must be ${BR_SPECIAL_NOTES_MAX_WORDS} words or fewer.`;
  }

  const validateExtraList = (list, label) => {
    for (let i = 0; i < list.length; i += 1) {
      const entry = list[i];
      if (isBrLocationEntryEmpty(entry)) continue;
      if (!isBrLocationEntryComplete(entry)) {
        return `Please complete all fields for ${label} additional address ${i + 1}, or remove it.`;
      }
      if (!isValidPincode(entry.pincode)) {
        return `${label} additional address ${i + 1} has an invalid pincode/ZIP.`;
      }
      if (countWords(entry.address) > BR_ADDRESS_MAX_WORDS) {
        return `${label} additional address ${i + 1} exceeds ${BR_ADDRESS_MAX_WORDS} words.`;
      }
    }
    return null;
  };

  if (includesOfficeRelocationScope(form.relocationScope)) {
    const currentErr = validateExtraList(form.additionalCurrentLocations || [], 'Current');
    if (currentErr) return currentErr;
    const destErr = validateExtraList(form.additionalDestLocations || [], 'Destination');
    if (destErr) return destErr;
  }

  return null;
}

export const BR_URGENCY_OPTIONS = ['', 'Standard', 'Express', 'Urgent'];

export const BR_BUDGET_RANGE_OPTIONS = [
  '',
  'Under $25,000',
  '$25,000 - $75,000',
  '$75,000 - $150,000',
  '$150,000 - $500,000',
  'Over $500,000',
];

export function createBrLocationEntry() {
  const id =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `loc-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  return {
    id,
    country: '',
    state: '',
    city: '',
    pincode: '',
    address: '',
  };
}

export function getInitialBusinessRelocationRfqForm(companyId) {
  return {
    companyId: companyId != null && Number.isFinite(Number(companyId)) ? Number(companyId) : null,
    rfqReferenceNumber: '',
    contactPerson: '',
    contactNumber: '',
    companyName: '',
    companyEmail: '',
    relocationScope: '',
    currentCountry: '',
    currentState: '',
    currentCity: '',
    currentPincode: '',
    currentAddress: '',
    additionalCurrentLocations: [],
    destCountry: '',
    destState: '',
    destCity: '',
    destPincode: '',
    destAddress: '',
    additionalDestLocations: [],
    totalWeightKg: '',
    totalWeightUnit: 'KG',
    totalPackages: '',
    totalValueUsd: '',
    totalValueCurrency: 'USD',
    preferredMoveDate: '',
    urgencyLevel: '',
    logisticsMode: '',
    budgetRange: '',
    specialNotes: '',
    additionalServices: {
      professionalPacking: false,
      unpackingSetup: false,
      tempStorage: false,
      insuranceCoverage: false,
      customsClearance: false,
      vehicleTransport: false,
      petRelocation: false,
      realTimeTracking: false,
    },
  };
}

export function generateBrRfqReference() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const r = Math.random().toString(36).slice(2, 8).toUpperCase();
  return `BRQ-${y}${m}${day}-${r}`;
}

export function includesOfficeRelocationScope(scope) {
  const s = String(scope || '').trim().toLowerCase();
  return s === 'office' || s === 'both';
}

export function isBrLocationEntryComplete(entry) {
  if (!entry) return false;
  return Boolean(
    String(entry.country || '').trim() &&
      String(entry.state || '').trim() &&
      String(entry.city || '').trim() &&
      String(entry.pincode || '').trim() &&
      String(entry.address || '').trim(),
  );
}

export function isBrLocationEntryEmpty(entry) {
  if (!entry) return true;
  return !(
    String(entry.country || '').trim() ||
    String(entry.state || '').trim() ||
    String(entry.city || '').trim() ||
    String(entry.pincode || '').trim() ||
    String(entry.address || '').trim()
  );
}

/** API payload shape (no client ids). */
export function buildSupplementalLocationsPayload(form) {
  const mapList = (list) =>
    (Array.isArray(list) ? list : [])
      .filter((e) => !isBrLocationEntryEmpty(e))
      .map((e) => ({
        country: String(e.country || '').trim(),
        state: String(e.state || '').trim(),
        city: String(e.city || '').trim(),
        pincode: String(e.pincode || '').trim(),
        address: String(e.address || '').trim(),
      }));

  return {
    current: mapList(form.additionalCurrentLocations),
    destination: mapList(form.additionalDestLocations),
  };
}
